<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  modelValue: { type: Array, required: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Espelham Csat::SurveyDefinition (MAX_QUESTIONS e MAX_SCALE_POINTS): o
// backend recusa no save, aqui o consultor vê antes de tentar.
const MAX_QUESTIONS = 10;
const MAX_SCALE_POINTS = 11;
const INTERACTIVE_LIMIT = 10;
// Espelha Csat::SurveyDefinition::MAX_LABEL_LENGTH (descrição da linha da
// lista do WhatsApp).
const MAX_LABEL_LENGTH = 72;
// Espelham MAX_TEXT_LENGTH/MAX_OPTION_LENGTH: o WhatsApp corta o que passar.
const MAX_TEXT_LENGTH = 1024;
const MAX_OPTION_LENGTH = 20;

const questions = computed(() => props.modelValue);

const typeOptions = computed(() => [
  { label: t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TYPE.SCALE'), value: 'scale' },
  { label: t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TYPE.CHOICE'), value: 'choice' },
  { label: t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TYPE.TEXT'), value: 'text' },
]);

const isScale = question => question.type === 'scale';
const isChoice = question => question.type === 'choice';

const scalePoints = question => Number(question.max) - Number(question.min) + 1;

// Legenda só faz sentido onde a nota vira lista tocável (até 10 valores).
const hasLabels = question =>
  isScale(question) && scalePoints(question) <= INTERACTIVE_LIMIT;

const scaleValues = question =>
  Array.from(
    { length: scalePoints(question) },
    (_value, offset) => Number(question.min) + offset
  );

// O backend recusa legenda fora da escala: ao mudar o início/fim, as que
// saíram do intervalo vão embora junto.
const labelsWithin = (labels, min, max) =>
  Object.fromEntries(
    Object.entries(labels || {}).filter(
      ([value]) => Number(value) >= min && Number(value) <= max
    )
  );

const rules = {
  modelValue: {
    $each: helpers.forEach({
      text: {
        required: helpers.withMessage(
          () => t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.EMPTY_TEXT'),
          required
        ),
        maxLength: helpers.withMessage(
          () =>
            t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TEXT_TOO_LONG', {
              max: MAX_TEXT_LENGTH,
            }),
          maxLength(MAX_TEXT_LENGTH)
        ),
      },
      max: {
        ordered: helpers.withMessage(
          () => t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.INVERTED'),
          (value, question) =>
            !isScale(question) || Number(question.min) < Number(value)
        ),
        width: helpers.withMessage(
          () =>
            t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.TOO_WIDE', {
              max_points: MAX_SCALE_POINTS,
            }),
          (value, question) =>
            !isScale(question) || scalePoints(question) <= MAX_SCALE_POINTS
        ),
      },
      options: {
        enough: helpers.withMessage(
          () => t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.OPTIONS.MIN_REQUIRED'),
          (value, question) =>
            !isChoice(question) ||
            (value || []).filter(option => option.trim()).length >= 2
        ),
        short: helpers.withMessage(
          () =>
            t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.OPTIONS.TOO_LONG', {
              max: MAX_OPTION_LENGTH,
            }),
          (value, question) =>
            !isChoice(question) ||
            (value || []).every(option => option.length <= MAX_OPTION_LENGTH)
        ),
      },
    }),
  },
};

const v$ = useVuelidate(rules, props);

const errorsFor = (index, field) => {
  const response = v$.value.modelValue.$each?.$response;
  if (!response) return [];

  return (response.$errors[index]?.[field] || []).map(error => error.$message);
};

const firstError = (index, field) => errorsFor(index, field)[0] ?? '';

const hasPrimary = computed(() => questions.value.some(q => q.primary));

const canAdd = computed(() => questions.value.length < MAX_QUESTIONS);

// Chave estável: o relatório agrupa por ela, então nunca muda quando o
// consultor reescreve o texto ou reordena as perguntas.
const nextKey = () => {
  const used = questions.value
    .map(question => Number(String(question.key).replace('pergunta_', '')))
    .filter(number => !Number.isNaN(number));

  return `pergunta_${used.length ? Math.max(...used) + 1 : 1}`;
};

const commit = list => emit('update:modelValue', list);

const patchQuestion = (index, patch) =>
  commit(
    questions.value.map((question, position) =>
      position === index ? { ...question, ...patch } : question
    )
  );

const addQuestion = () =>
  commit([
    ...questions.value,
    {
      key: nextKey(),
      text: '',
      type: 'scale',
      min: 1,
      max: 5,
      options: [],
      labels: {},
      primary: !hasPrimary.value,
    },
  ]);

const removeQuestion = index =>
  commit(questions.value.filter((_question, position) => position !== index));

const moveQuestion = (index, offset) => {
  const target = index + offset;
  if (target < 0 || target >= questions.value.length) return;

  const list = [...questions.value];
  [list[index], list[target]] = [list[target], list[index]];
  commit(list);
};

// Só uma pergunta alimenta o indicador, e ela precisa ser do tipo Nota.
const setPrimary = index =>
  commit(
    questions.value.map((question, position) => ({
      ...question,
      primary: position === index,
    }))
  );

const changeScale = (index, edge, value) => {
  const question = questions.value[index];
  const min = edge === 'min' ? value : Number(question.min);
  const max = edge === 'max' ? value : Number(question.max);
  patchQuestion(index, {
    [edge]: value,
    labels: labelsWithin(question.labels, min, max),
  });
};

const updateLabel = (index, value, text) => {
  const labels = { ...(questions.value[index].labels || {}) };
  if (text) {
    labels[value] = text.slice(0, MAX_LABEL_LENGTH);
  } else {
    delete labels[value];
  }
  patchQuestion(index, { labels });
};

const changeType = (index, type) => {
  const patch = { type };
  if (type === 'scale') {
    patch.min = questions.value[index].min ?? 1;
    patch.max = questions.value[index].max ?? 5;
  } else {
    patch.primary = false;
  }
  patchQuestion(index, patch);
};

const updateOption = (index, optionIndex, value) => {
  const options = [...(questions.value[index].options || [])];
  options[optionIndex] = value;
  patchQuestion(index, { options });
};

const addOption = index =>
  patchQuestion(index, {
    options: [...(questions.value[index].options || []), ''],
  });

const removeOption = (index, optionIndex) =>
  patchQuestion(index, {
    options: questions.value[index].options.filter(
      (_option, position) => position !== optionIndex
    ),
  });

const validate = () => v$.value.$validate();

defineExpose({ validate });
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.DESCRIPTION') }}
    </p>

    <div
      v-for="(question, index) in questions"
      :key="question.key"
      class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-slate-2"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.POSITION', {
              position: index + 1,
            })
          }}
        </span>
        <div class="flex items-center gap-1">
          <NextButton
            v-tooltip="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.MOVE_UP')"
            xs
            ghost
            slate
            icon="i-lucide-arrow-up"
            :disabled="index === 0"
            @click="moveQuestion(index, -1)"
          />
          <NextButton
            v-tooltip="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.MOVE_DOWN')"
            xs
            ghost
            slate
            icon="i-lucide-arrow-down"
            :disabled="index === questions.length - 1"
            @click="moveQuestion(index, 1)"
          />
          <NextButton
            v-tooltip="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.REMOVE')"
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            @click="removeQuestion(index)"
          />
        </div>
      </div>

      <Input
        :model-value="question.text"
        :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TEXT.LABEL')"
        :placeholder="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TEXT.PLACEHOLDER')"
        :message="firstError(index, 'text')"
        :message-type="firstError(index, 'text') ? 'error' : 'info'"
        @update:model-value="patchQuestion(index, { text: $event })"
      />

      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.TYPE.LABEL') }}
        </span>
        <FilterSelect
          :model-value="question.type"
          variant="faded"
          :options="typeOptions"
          class="inline-flex shrink-0 w-fit"
          @update:model-value="changeType(index, $event)"
        />
      </div>

      <div v-if="isScale(question)" class="flex flex-col gap-2">
        <div class="flex items-end gap-3">
          <Input
            :model-value="question.min"
            type="number"
            class="w-24"
            :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.MIN')"
            @update:model-value="changeScale(index, 'min', Number($event))"
          />
          <Input
            :model-value="question.max"
            type="number"
            class="w-24"
            :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.MAX')"
            :message="firstError(index, 'max')"
            :message-type="firstError(index, 'max') ? 'error' : 'info'"
            @update:model-value="changeScale(index, 'max', Number($event))"
          />
        </div>
        <p
          v-if="scalePoints(question) > INTERACTIVE_LIMIT"
          class="text-sm text-n-amber-11"
        >
          {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.TEXT_ONLY') }}
        </p>
        <div v-if="hasLabels(question)" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.LABELS.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.LABELS.HINT', {
                max: MAX_LABEL_LENGTH,
              })
            }}
          </span>
          <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div
              v-for="value in scaleValues(question)"
              :key="value"
              class="flex items-center gap-2"
            >
              <span class="w-6 text-sm text-right text-n-slate-11 shrink-0">
                {{ value }}
              </span>
              <Input
                :model-value="question.labels?.[value] || ''"
                class="flex-1"
                :placeholder="
                  $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.SCALE.LABELS.PLACEHOLDER')
                "
                @update:model-value="updateLabel(index, value, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="isChoice(question)" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.OPTIONS.LABEL') }}
        </span>
        <div
          v-for="(option, optionIndex) in question.options"
          :key="optionIndex"
          class="flex items-center gap-2"
        >
          <Input
            :model-value="option"
            class="flex-1"
            :placeholder="
              $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.OPTIONS.PLACEHOLDER')
            "
            @update:model-value="updateOption(index, optionIndex, $event)"
          />
          <NextButton
            xs
            ghost
            ruby
            icon="i-lucide-x"
            @click="removeOption(index, optionIndex)"
          />
        </div>
        <NextButton
          sm
          faded
          slate
          icon="i-lucide-plus"
          class="w-fit"
          :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.OPTIONS.ADD')"
          @click="addOption(index)"
        />
        <p v-if="firstError(index, 'options')" class="text-sm text-n-ruby-11">
          {{ firstError(index, 'options') }}
        </p>
      </div>

      <label
        v-if="isScale(question)"
        class="flex items-start gap-2 cursor-pointer"
      >
        <input
          type="radio"
          class="mt-1"
          :checked="question.primary"
          @change="setPrimary(index)"
        />
        <span class="flex flex-col">
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.PRIMARY.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.PRIMARY.HINT') }}
          </span>
        </span>
      </label>
    </div>

    <p v-if="questions.length && !hasPrimary" class="text-sm text-n-amber-11">
      {{ $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.PRIMARY.MISSING') }}
    </p>

    <NextButton
      v-if="canAdd"
      sm
      faded
      slate
      icon="i-lucide-plus"
      class="w-fit"
      :label="$t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.ADD')"
      @click="addQuestion"
    />
    <p v-else class="text-sm text-n-slate-11">
      {{
        $t('INBOX_MGMT.CSAT.CHAT.QUESTIONS.LIMIT_REACHED', {
          max: MAX_QUESTIONS,
        })
      }}
    </p>
  </div>
</template>
